import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Button, Grid } from '@mui/material';
import { Fragment } from 'react';
import FieldNames from '../../../defaults/FieldNames.json';
import { formatDateForDisplay } from '../../../utils/DateHelper';
import { getAllMissingFieldsCount } from '../../../utils/FeatureHelper';
import fieldsDefinition from '../ProjectFieldInfo.json';
import DetailsCheckbox from './DetailsCheckbox';
import DetailsDatePicker from './DetailsDatePicker';
import './DetailsGridItem.css';
import DetailsPopover from './DetailsPopover';
import DetailsText from './DetailsText';
import DetailsTextArea from './DetailsTextArea';

export default function DetailsGridProjectItem({
    field,
    mode,
    project,
    userInfo,
    userRoleInfo,
    onFieldChange,
    verifyName,
    isNameVerified,
    requiredFields,
}) {
    const currentfieldInfo = fieldsDefinition[field];

    const isFirePerimeter =
        project?.attributes?.[FieldNames.projectFields.isFirePerimeter] === FieldNames.yesNo.yes
            ? true
            : false;

    const fieldIsStatic = isFirePerimeter
        ? FieldNames.fireProjectStaticFields
        : FieldNames.nonFireProjectStaticFields;

    const getReadOnlyString = (fieldName) => {
        if (fieldName === FieldNames.projectFields.verify) {
            return ``;
        } else {
            if (project?.attributes) {
                let finalValue = project.attributes[fieldName]
                    ? project.attributes[fieldName]
                    : '--';
                if (fieldName === FieldNames.projectFields.id) {
                    finalValue =
                        project.attributes[FieldNames.featureFields.id] ??
                        project.attributes[FieldNames.projectFields.id];
                } else if (
                    fieldName === FieldNames.projectFields.fireDiscoveryDate ||
                    fieldName === FieldNames.projectFields.containmentDate
                ) {
                    finalValue = project.attributes[fieldName]
                        ? formatDateForDisplay(project.attributes[fieldName])
                        : '--';
                } else if (currentfieldInfo?.domain && currentfieldInfo?.domain.codedValues) {
                    const codes = currentfieldInfo?.domain.codedValues;
                    codes.forEach((element) => {
                        if (project.attributes[fieldName] === element.code) {
                            finalValue = element.name;
                        }
                    });
                } else if (fieldName === FieldNames.projectFields.isFirePerimeter) {
                    if (project.attributes[fieldName] === FieldNames.yesNo.yes) {
                        finalValue = FieldNames.yesNo.yes;
                    } else {
                        finalValue = FieldNames.yesNo.no;
                    }
                } else if (
                    fieldName === FieldNames.projectFields.lastModifiedDate ||
                    fieldName === FieldNames.projectFields.createdOnDate
                ) {
                    finalValue = formatDateForDisplay(project.attributes[fieldName]);
                } else if (project.attributes[fieldName]) {
                    finalValue = project.attributes[fieldName];
                } else {
                    finalValue = '--';
                }
                return finalValue;
            } else {
                return '--';
            }
        }
    };

    const getEditControlForField = (fieldInfo) => {
        let isRequiredField = false;
        if (requiredFields && requiredFields.length) {
            isRequiredField = requiredFields.includes(field);
        }
        if (project?.attributes) {
            if (fieldInfo) {
                if (fieldInfo === FieldNames.projectFields.isFirePerimeter) {
                    return (
                        <DetailsCheckbox
                            detail={project}
                            field={field}
                            onFieldChange={null}
                            allFields={FieldNames.projectFields}
                            fieldIsStatic={fieldIsStatic.includes(field)}
                        />
                    );
                } else if (fieldInfo === FieldNames.projectFields.notes) {
                    return (
                        <DetailsTextArea
                            detail={project}
                            field={field}
                            onFieldChange={onFieldChange}
                            isRequired={isRequiredField}
                            fieldIsStatic={fieldIsStatic.includes(field)}
                            getAllApprovalFieldsCount={0}
                            isActuals={false}
                            isProject={true}
                        />
                    );
                } else if (
                    fieldInfo === FieldNames.projectFields.lastModifiedDate ||
                    fieldInfo === FieldNames.projectFields.createdOnDate ||
                    fieldInfo === FieldNames.projectFields.fireDiscoveryDate ||
                    fieldInfo === FieldNames.projectFields.containmentDate
                ) {
                    return (
                        <DetailsDatePicker
                            detail={project}
                            field={field}
                            onFieldChange={onFieldChange}
                            isLocallyApproved={false}
                            setIsLocallyApproved={null}
                            isRegionalApproved={false}
                            setIsRegionalApproved={null}
                            isAgencyApproved={false}
                            setIsAgencyApproved={null}
                            isDepartmentApproved={false}
                            setIsDepartmentApproved={null}
                            isRequired={false}
                            getAllMissingFieldsCount={getAllMissingFieldsCount}
                            allFields={FieldNames.projectFields}
                            getAllApprovalFieldsCount={0}
                            isPast={false}
                            userInfo={userInfo}
                            userRoleInfo={userRoleInfo}
                            isCancelled={false}
                            onFiscalFieldChange={null}
                            isActuals={false}
                            departmentValue={null}
                            agencyValue={null}
                            regionValue={null}
                            unitValue={null}
                            fundingTribeValue={null}
                            isTreatment={false}
                        />
                    );
                } else if (fieldInfo === FieldNames.projectFields.verify) {
                    if (isNameVerified === FieldNames.yesNo.yes) {
                        return <CheckBoxIcon color='primary' />;
                    } else {
                        return <CheckBoxOutlineBlankIcon color='primary' />;
                    }
                }
            }

            return (
                <DetailsText
                    detail={project}
                    field={field}
                    onFieldChange={onFieldChange}
                    isRequired={isRequiredField}
                    fieldIsStatic={fieldIsStatic.includes(field)}
                    getAllApprovalFieldsCount={0}
                    isProject={true}
                />
            );
        } else {
            return '';
        }
    };

    const getLabel = () => {
        if (field === FieldNames.projectFields.isFirePerimeter) {
            return `Is From Fire Perimeter`;
        } else {
            if (currentfieldInfo) {
                return `${currentfieldInfo.alias}`;
            } else {
                return `**${field}`;
            }
        }
    };

    const formLabel = () => {
        if (requiredFields && requiredFields.length && requiredFields.includes(field)) {
            return <span className='required'>*</span>;
        } else {
            return <span></span>;
        }
    };

    const getButton = () => {
        if (mode === FieldNames.modes.read) {
            return ``;
        } else {
            return (
                <Button
                    color='primary'
                    variant='contained'
                    onClick={() => {
                        verifyName();
                    }}
                    aria-label='cancel'
                >
                    Verify
                </Button>
            );
        }
    };

    return (
        <Fragment>
            <Grid
                item
                size={12}
                container
                direction='row'
                className='grid-item'
                style={{ alignItems: 'center', minWidth: '550px', paddingRight: '5px' }}
            >
                <Grid item size={6} style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                    <span>
                        <label>
                            {field === FieldNames.projectFields.verify ? getButton() : getLabel()}
                            {formLabel()}
                        </label>
                        <DetailsPopover
                            mode={mode}
                            field={field}
                            isTreatment={false}
                            categoryValue={''}
                            isPoint={FieldNames.yesNo.no}
                            isActuals={false}
                            agencyValue={null}
                            isBulkApproval={false}
                        />
                    </span>
                </Grid>
                <Grid item size={6} style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                    {project?.attributes
                        ? mode === FieldNames.modes.edit
                            ? getEditControlForField(field)
                            : getReadOnlyString(field)
                        : '--'}
                </Grid>
            </Grid>
        </Fragment>
    );
}
